import { QuizData } from '@/components/ui/content-editor/types';

/**
 * Sort quiz questions by ID (ascending)
 * @param quiz The quiz data to sort
 * @returns A new QuizData object with questions sorted by ID
 */
export const sortQuizQuestionsById = (quiz: QuizData): QuizData => {
  if (!quiz || !quiz.questions || quiz.questions.length === 0) {
    return quiz;
  }

  return {
    ...quiz,
    questions: [...quiz.questions].sort((a, b) => a.id.localeCompare(b.id))
  };
};

/**
 * Sort quiz answers by ID (ascending) for each question
 * @param quiz The quiz data to sort
 * @returns A new QuizData object with answers sorted by ID
 */
export const sortQuizAnswersById = (quiz: QuizData): QuizData => {
  if (!quiz || !quiz.questions || quiz.questions.length === 0) {
    return quiz;
  }

  return {
    ...quiz,
    questions: quiz.questions.map(question => ({
      ...question,
      options: [...question.options].sort((a, b) => a.id.localeCompare(b.id))
    }))
  };
};

/**
 * Randomize the order of quiz questions
 * @param quiz The quiz data to randomize
 * @returns A new QuizData object with questions in random order
 */
export const randomizeQuizQuestions = (quiz: QuizData): QuizData => {
  if (!quiz || !quiz.questions || quiz.questions.length === 0) {
    return quiz;
  }

  // Fisher-Yates shuffle algorithm
  const shuffled = [...quiz.questions];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return {
    ...quiz,
    questions: shuffled
  };
};

/**
 * Apply admin view ordering: sort both questions and answers by ID
 * @param quiz The quiz data to order
 * @returns A new QuizData object with sorted questions and answers
 */
export const applyAdminViewOrdering = (quiz: QuizData): QuizData => {
  return sortQuizAnswersById(sortQuizQuestionsById(quiz));
};

/**
 * Apply student view ordering: randomize questions but sort answers by ID
 * @param quiz The quiz data to order
 * @returns A new QuizData object with randomized questions and sorted answers
 */
export const applyStudentViewOrdering = (quiz: QuizData): QuizData => {
  return sortQuizAnswersById(randomizeQuizQuestions(quiz));
}; 